import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';

import { formatCompactOrIsoDate, formatCurrencyAmount } from '../../shared/trade-formatters';
import { CatalogCrudModalComponent } from '../../catalog/shared/components/catalog-crud-modal';
import { CatalogDeleteConfirmModalComponent } from '../../catalog/shared/components/catalog-delete-confirm-modal';
import { CatalogModalController } from '../../catalog/shared/catalog-modal.controller';
import { SwapDetailService } from './swap-detail.service';
import {
  DocumentCreateRequest,
  DocumentUpdateRequest,
  SwapActionItem,
  SwapActionTypeOption,
  SwapActionUpsertRequest,
  SwapDetail as SwapDetailModel,
  SwapDetailSaveRequest,
  SwapDocumentItem,
  SwapExtLocalSaveRequest,
  SwapFeeItem,
  SwapFeeTypeOption,
  SwapFeeUpsertRequest,
  SwapFormulaItem,
  SwapNoteItem,
  SwapNoteUpsertRequest,
  SwapPurposeSubtypeOption,
} from './swap-detail.types';
import { resolveApiErrorMessage } from '../../shared/api-error';
import {
  buildDocumentUploadToast,
  createDocumentUploadEntries,
  DocumentUploadEntry,
  summarizeDocumentUploads,
  updateDocumentUploadEntry,
  uploadDocumentsSequentially,
} from '../../shared/document-upload';
import { DocumentUploadList } from '../../shared/document-upload-list';
import { DocumentNameCell } from '../../shared/document-name-cell';
import { DocumentDescriptionCell } from '../../shared/document-description-cell';

interface SwapDetailSection {
  id: SwapDetailSectionId;
  label: string;
  panelId: string;
  iconClass: string;
}

type SwapDetailSectionId =
  | 'swap-details'
  | 'attached-documentation'
  | 'actions'
  | 'notes';

interface KTTabsInstance {
  show: (tabElement: HTMLElement) => void;
  dispose?: () => void;
}

interface KTTabsConstructor {
  createInstances?: () => void;
  getOrCreateInstance?: (element: HTMLElement) => KTTabsInstance;
}

interface SwapFeeFormState {
  feeBpsId: string;
  value: string;
}

interface SwapNoteFormState {
  note: string;
}

interface SwapDocumentFormState {
  description: string;
  filename: string;
  files: File[];
}

interface SwapActionFormState {
  tradeDate: string;
  effectiveDate: string;
  actionTypeId: string;
  actionTypeDescription: string;
  comments: string;
}

@Component({
  selector: 'app-swap-detail',
  imports: [
    CommonModule,
    RouterLink,
    CatalogCrudModalComponent,
    CatalogDeleteConfirmModalComponent,
    DocumentUploadList,
    DocumentNameCell,
    DocumentDescriptionCell,
  ],
  templateUrl: './swap-detail.html',
  styleUrl: './swap-detail.css',
})
export class SwapDetail implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tabsRoot')
  private tabsRoot?: ElementRef<HTMLElement>;

  @ViewChild('documentFilesInput')
  private documentFilesInput?: ElementRef<HTMLInputElement>;

  private tabsInstance: KTTabsInstance | null = null;
  private detailSubscription?: Subscription;
  private saveSubscription?: Subscription;
  private initializeSubscription?: Subscription;
  private swapFeeMutationSubscription?: Subscription;
  private swapFeeTypesSubscription?: Subscription;
  private swapActionTypesSubscription?: Subscription;
  private swapActionMutationSubscription?: Subscription;
  private documentsSubscription?: Subscription;
  private documentMutationSubscription?: Subscription;
  private notesSubscription?: Subscription;
  private noteMutationSubscription?: Subscription;
  private addDocumentModalCloseObserver?: MutationObserver;
  private isHydrating = false;
  private readonly modalController = new CatalogModalController();
  private readonly formulasModalId = 'swap_detail_formulas_modal';
  private readonly swapFeeModalId = 'swap_detail_fee_modal';
  private readonly deleteSwapFeeModalId = 'swap_detail_delete_fee_modal';
  private readonly swapActionModalId = 'swap_detail_action_modal';
  private readonly deleteSwapActionModalId = 'swap_detail_delete_action_modal';
  private readonly addDocumentModalId = 'swap_detail_add_document_modal';
  private readonly deleteDocumentModalId = 'swap_detail_delete_document_modal';
  private readonly addNoteModalId = 'swap_detail_add_note_modal';
  private readonly deleteNoteModalId = 'swap_detail_delete_note_modal';
  private readonly notesSource = 'Swap';
  private readonly documentSource = 'Swap';
  private readonly allowedDocumentTypes = ['.doc', '.msg', '.pdf', '.xls'];

  protected readonly tradeId: string;
  protected readonly sections: SwapDetailSection[] = [
    { id: 'swap-details', label: 'Swap Details', panelId: 'swap-detail-tab-swap-details', iconClass: 'ki-setting-2' },
    {
      id: 'attached-documentation',
      label: 'Documentation',
      panelId: 'swap-detail-tab-attached-documentation',
      iconClass: 'ki-file-up',
    },
    { id: 'actions', label: 'Actions', panelId: 'swap-detail-tab-actions', iconClass: 'ki-people' },
    { id: 'notes', label: 'Notes', panelId: 'swap-detail-tab-notes', iconClass: 'ki-notepad' },
  ];

  protected readonly isLoading = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly isInitializing = signal(false);
  protected readonly initializeError = signal('');
  protected readonly errorMessage = signal('');
  protected readonly saveError = signal('');
  protected readonly saveSuccessMessage = signal('');
  protected readonly hasUnsavedChanges = signal(false);
  protected readonly detail = signal<SwapDetailModel | null>(null);
  protected readonly selectedFormulas = signal<SwapFormulaItem[]>([]);
  protected readonly selectedFormulasTitle = signal('');
  protected readonly swapFeeTypes = signal<SwapFeeTypeOption[]>([]);
  protected readonly swapActionTypes = signal<SwapActionTypeOption[]>([]);
  protected readonly isMutatingSwapFee = signal(false);
  protected readonly swapFeeFormError = signal('');
  protected readonly editingSwapFeeId = signal<number | null>(null);
  protected readonly pendingSwapFeeDeleteId = signal<number | null>(null);
  protected readonly pendingSwapFeeDeleteName = signal('');
  protected readonly isMutatingSwapAction = signal(false);
  protected readonly swapActionFormError = signal('');
  protected readonly editingSwapActionId = signal<number | null>(null);
  protected readonly pendingSwapActionDeleteId = signal<number | null>(null);
  protected readonly pendingSwapActionDeleteName = signal('');
  protected readonly isMutatingDocument = signal(false);
  protected readonly renamingDocumentId = signal<number | null>(null);
  protected readonly describingDocumentId = signal<number | null>(null);
  protected readonly documentUploadEntries = signal<DocumentUploadEntry[]>([]);
  protected readonly documentFormError = signal('');
  protected readonly editingDocumentId = signal<number | null>(null);
  protected readonly pendingDocumentDeleteId = signal<number | null>(null);
  protected readonly pendingDocumentDeletePreview = signal('');
  protected readonly isMutatingNote = signal(false);
  protected readonly noteFormError = signal('');
  protected readonly editingNoteId = signal<number | null>(null);
  protected readonly pendingNoteDeleteId = signal<number | null>(null);
  protected readonly pendingNoteDeletePreview = signal('');
  protected readonly swapFeeForm = signal<SwapFeeFormState>({
    feeBpsId: '',
    value: '',
  });
  protected readonly swapActionForm = signal<SwapActionFormState>({
    tradeDate: '',
    effectiveDate: '',
    actionTypeId: '',
    actionTypeDescription: '',
    comments: '',
  });
  protected readonly addNoteForm = signal<SwapNoteFormState>({
    note: '',
  });
  protected readonly addDocumentForm = signal<SwapDocumentFormState>({
    description: '',
    filename: '',
    files: [],
  });

  protected readonly purposeId = signal<number | null>(null);
  protected readonly purposeSubtypeId = signal<number | null>(null);
  protected readonly comments = signal('');
  protected readonly genericField1 = signal('');
  protected readonly genericField2 = signal('');
  protected readonly genericField3 = signal('');
  protected readonly hasTermSheet = signal(false);
  protected readonly swapLeg = signal<'PAY' | 'RECEIVE'>('PAY');
  protected readonly bpsPerYear = signal('');
  protected readonly swapTheoreticalFee = signal('');
  protected readonly tFeeCurrency = signal('');

  protected readonly filteredPurposeSubtypes = computed<SwapPurposeSubtypeOption[]>(() => {
    const currentDetail = this.detail();
    if (!currentDetail) {
      return [];
    }

    const selectedPurposeId = this.purposeId();
    if (selectedPurposeId === null) {
      return currentDetail.swapExtLocal.purposeSubtypes;
    }

    return currentDetail.swapExtLocal.purposeSubtypes.filter((item) => item.purposeId === selectedPurposeId);
  });
  protected readonly legOtherLabel = computed(() => (this.swapLeg() === 'PAY' ? 'Pay Leg Other' : 'Receive Leg Other'));
  protected readonly swapFeeModalTitle = computed(() => (this.editingSwapFeeId() === null ? 'Add Swap Charges (BPS per year)' : 'Edit Swap Charges (BPS per year)'));
  protected readonly swapFeeModalSubmitLabel = computed(() => (this.editingSwapFeeId() === null ? 'Add' : 'Save Changes'));
  protected readonly swapActionModalTitle = computed(() => (this.editingSwapActionId() === null ? 'Add Action' : 'Edit Action'));
  protected readonly swapActionModalSubmitLabel = computed(() => (this.editingSwapActionId() === null ? 'Add' : 'Save Changes'));
  protected readonly documentModalTitle = computed(() => (this.editingDocumentId() === null ? 'Upload Documentation' : 'Edit Documentation'));
  protected readonly documentModalSubmitLabel = computed(() => (this.editingDocumentId() === null ? 'Upload Documents' : 'Save Changes'));
  protected readonly noteModalTitle = computed(() => (this.editingNoteId() === null ? 'Add Note' : 'Edit Note'));
  protected readonly noteModalSubmitLabel = computed(() => (this.editingNoteId() === null ? 'Add Note' : 'Save Changes'));

  constructor(
    private readonly route: ActivatedRoute,
    private readonly titleService: Title,
    private readonly swapDetailService: SwapDetailService,
  ) {
    this.tradeId = this.route.snapshot.paramMap.get('tradeId') ?? '';
  }

  @HostListener('window:beforeunload', ['$event'])
  protected handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges()) {
      return;
    }

    event.preventDefault();
    event.returnValue = '';
  }

  ngOnInit(): void {
    this.updateDocumentTitle('');
    this.loadSwapFeeTypes();
    this.loadSwapActionTypes();
    this.loadDetail();
  }

  ngAfterViewInit(): void {
    this.initializeKTTabs();
    this.addDocumentModalCloseObserver = this.modalController.observeClose(this.addDocumentModalId, () => {
      this.resetAddDocumentForm();
    });
  }

  ngOnDestroy(): void {
    this.titleService.setTitle('FRIDA');
    this.detailSubscription?.unsubscribe();
    this.saveSubscription?.unsubscribe();
    this.initializeSubscription?.unsubscribe();
    this.swapFeeMutationSubscription?.unsubscribe();
    this.swapFeeTypesSubscription?.unsubscribe();
    this.swapActionTypesSubscription?.unsubscribe();
    this.swapActionMutationSubscription?.unsubscribe();
    this.documentsSubscription?.unsubscribe();
    this.documentMutationSubscription?.unsubscribe();
    this.notesSubscription?.unsubscribe();
    this.noteMutationSubscription?.unsubscribe();
    this.addDocumentModalCloseObserver?.disconnect();
    this.tabsInstance?.dispose?.();
  }

  protected onRetry(): void {
    this.loadDetail();
  }

  protected onSave(): void {
    const currentDetail = this.detail();
    if (!currentDetail || !this.tradeId) {
      return;
    }

    this.isSaving.set(true);
    this.saveError.set('');
    this.saveSuccessMessage.set('');
    this.saveSubscription?.unsubscribe();

    const payload = this.toSwapDetailSaveRequest(currentDetail);

    this.saveSubscription = this.swapDetailService.saveSwapDetail(payload).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.hasUnsavedChanges.set(false);
        this.saveSuccessMessage.set('Swap detail saved successfully.');
        window.KTToast?.show({
          message: 'Swap detail saved successfully.',
          variant: 'success',
        });
        this.loadDetail();
      },
      error: () => {
        this.isSaving.set(false);
        this.saveError.set('Unable to save swap detail. Please try again.');
        window.KTToast?.show({
          message: 'Unable to save swap detail. Please try again.',
          variant: 'destructive',
        });
      },
    });
  }

  protected onProceedWithNewSwap(): void {
    if (!this.tradeId || this.isInitializing()) {
      return;
    }

    const currentDetail = this.detail();
    if (!currentDetail) {
      this.initializeError.set('Swap detail is unavailable. Please refresh and try again.');
      return;
    }

    this.isInitializing.set(true);
    this.initializeError.set('');
    this.initializeSubscription?.unsubscribe();

    const payload = this.toSwapDetailSaveRequest(currentDetail);

    this.initializeSubscription = this.swapDetailService.saveSwapDetail(payload).subscribe({
      next: () => {
        this.isInitializing.set(false);
        this.hasUnsavedChanges.set(false);
        window.KTToast?.show({
          message: 'Swap detail was registered in FRIDA.',
          variant: 'success',
        });
        this.loadDetail();
      },
      error: () => {
        this.isInitializing.set(false);
        this.initializeError.set('Unable to register this swap in FRIDA. Please try again.');
        window.KTToast?.show({
          message: 'Unable to register this swap in FRIDA. Please try again.',
          variant: 'destructive',
        });
      },
    });
  }

  private toSwapDetailSaveRequest(detail: SwapDetailModel): SwapDetailSaveRequest {
    return {
      tradeId: detail.tradeId || this.tradeId,
      isNew: detail.isNew,
      hasTermSheet: this.hasTermSheet(),
      pendingChildren: detail.pendingChildren,
      swapExtLocal: this.toSwapExtLocalSaveRequest(detail),
    };
  }

  protected onPurposeChange(value: string): void {
    const parsed = this.toNullableInteger(value);
    this.purposeId.set(parsed);

    if (parsed === null) {
      this.purposeSubtypeId.set(null);
      this.markDirty();
      return;
    }

    const subtypeStillValid = this.filteredPurposeSubtypes().some((item) => item.id === this.purposeSubtypeId());
    if (!subtypeStillValid) {
      this.purposeSubtypeId.set(null);
    }

    this.markDirty();
  }

  protected onPurposeSubtypeChange(value: string): void {
    this.purposeSubtypeId.set(this.toNullableInteger(value));
    this.markDirty();
  }

  protected isPurposeSelected(optionId: number): boolean {
    return this.purposeId() === optionId;
  }

  protected isPurposeSubtypeSelected(optionId: number): boolean {
    return this.purposeSubtypeId() === optionId;
  }

  protected onCommentsChange(value: string): void {
    this.comments.set(value);
    this.markDirty();
  }

  protected onGenericField1Change(value: string): void {
    this.genericField1.set(value);
    this.markDirty();
  }

  protected onGenericField2Change(value: string): void {
    this.genericField2.set(value);
    this.markDirty();
  }

  protected onGenericField3Change(value: string): void {
    this.genericField3.set(value);
    this.markDirty();
  }

  protected onSwapLegChange(value: string): void {
    this.swapLeg.set(value === 'RECEIVE' ? 'RECEIVE' : 'PAY');
    this.syncLegDependentValues(false);
    this.recalculateTheoreticalFee();
    this.markDirty();
  }

  protected onBpsPerYearChange(value: string): void {
    this.bpsPerYear.set(value);
    this.recalculateTheoreticalFee();
    this.markDirty();
  }

  protected onSwapTheoreticalFeeChange(value: string): void {
    this.swapTheoreticalFee.set(value);
    this.markDirty();
  }

  protected formatDate(value: string): string {
    return formatCompactOrIsoDate(value);
  }

  protected formatDateTime(value: string): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  }

  protected formatDateTimeOrBlank(value: string): string {
    if (!value) {
      return '';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  }

  protected formatCurrency(value: number | null, currency: string): string {
    if (value === null) {
      return '-';
    }

    return formatCurrencyAmount(currency, value);
  }

  protected formatNumber(value: number | null, maxFractionDigits = 2): string {
    if (value === null) {
      return '-';
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxFractionDigits,
    }).format(value);
  }

  protected trackFee(index: number, item: SwapFeeItem): string {
    return `${item.id}-${item.tradeId}-${index}`;
  }

  protected resetSwapFeeForm(): void {
    if (this.isMutatingSwapFee()) {
      return;
    }

    this.swapFeeFormError.set('');
    this.editingSwapFeeId.set(null);
    this.swapFeeForm.set({
      feeBpsId: '',
      value: '',
    });
  }

  protected startEditSwapFee(fee: SwapFeeItem): void {
    if (this.isMutatingSwapFee()) {
      return;
    }

    this.swapFeeFormError.set('');
    this.editingSwapFeeId.set(fee.id);
    this.swapFeeForm.set({
      feeBpsId: fee.feeBpsId === null ? '' : String(fee.feeBpsId),
      value: fee.value === null ? '' : String(fee.value),
    });
  }

  protected updateSwapFeeField(field: keyof SwapFeeFormState, value: string): void {
    this.swapFeeForm.update((current) => ({
      ...current,
      [field]: value,
    }));
  }

  protected submitSwapFee(): void {
    const currentDetail = this.detail();
    if (!currentDetail || this.isMutatingSwapFee()) {
      return;
    }

    const payload = this.toSwapFeeUpsertRequest(currentDetail.tradeId || this.tradeId);
    if (!payload) {
      return;
    }

    const editingId = this.editingSwapFeeId();
    this.isMutatingSwapFee.set(true);
    this.swapFeeFormError.set('');
    this.swapFeeMutationSubscription?.unsubscribe();

    if (editingId === null) {
      this.swapFeeMutationSubscription = this.swapDetailService.createSwapFee(payload).subscribe({
        next: (createdFee) => {
          this.upsertSwapFeeRow(createdFee);
          this.isMutatingSwapFee.set(false);
          this.resetSwapFeeForm();
          this.modalController.close(this.swapFeeModalId);
          window.KTToast?.show({
            message: 'Swap charge added successfully.',
            variant: 'success',
          });
        },
        error: (error: unknown) => {
          this.isMutatingSwapFee.set(false);
          this.swapFeeFormError.set(resolveApiErrorMessage('Unable to add swap charge. Please try again.', error));
        },
      });
      return;
    }

    this.swapFeeMutationSubscription = this.swapDetailService.updateSwapFee(editingId, payload).subscribe({
      next: () => {
        const currentRow = currentDetail.swapFees.find((item) => item.id === editingId);
        this.upsertSwapFeeRow({
          id: editingId,
          tradeId: payload.tradeId,
          feeBpsId: payload.feeBpsId,
          value: payload.value,
          feeTypeName: this.resolveSwapFeeTypeName(payload.feeBpsId, currentRow?.feeTypeName),
        });
        this.isMutatingSwapFee.set(false);
        this.resetSwapFeeForm();
        this.modalController.close(this.swapFeeModalId);
        window.KTToast?.show({
          message: 'Swap charge updated successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingSwapFee.set(false);
        this.swapFeeFormError.set(resolveApiErrorMessage('Unable to update swap charge. Please try again.', error));
      },
    });
  }

  protected requestDeleteSwapFee(fee: SwapFeeItem): void {
    if (this.isMutatingSwapFee()) {
      return;
    }

    this.pendingSwapFeeDeleteId.set(fee.id);
    this.pendingSwapFeeDeleteName.set(fee.feeTypeName || `Fee ${fee.id}`);
    this.swapFeeFormError.set('');
    this.modalController.open(this.deleteSwapFeeModalId);
  }

  protected cancelDeleteSwapFee(): void {
    if (this.isMutatingSwapFee()) {
      return;
    }

    this.pendingSwapFeeDeleteId.set(null);
    this.pendingSwapFeeDeleteName.set('');
  }

  protected confirmDeleteSwapFee(): void {
    const feeId = this.pendingSwapFeeDeleteId();
    if (feeId === null || this.isMutatingSwapFee()) {
      return;
    }

    this.isMutatingSwapFee.set(true);
    this.swapFeeFormError.set('');
    this.swapFeeMutationSubscription?.unsubscribe();
    this.swapFeeMutationSubscription = this.swapDetailService.deleteSwapFee(feeId).subscribe({
      next: () => {
        this.removeSwapFeeRow(feeId);
        this.pendingSwapFeeDeleteId.set(null);
        this.pendingSwapFeeDeleteName.set('');
        this.isMutatingSwapFee.set(false);
        this.modalController.close(this.deleteSwapFeeModalId);
        window.KTToast?.show({
          message: 'Swap charge deleted successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingSwapFee.set(false);
        this.swapFeeFormError.set(resolveApiErrorMessage('Unable to delete swap charge. Please try again.', error));
      },
    });
  }

  protected resetSwapActionForm(): void {
    if (this.isMutatingSwapAction()) {
      return;
    }

    this.swapActionFormError.set('');
    this.editingSwapActionId.set(null);
    this.swapActionForm.set({
      tradeDate: '',
      effectiveDate: '',
      actionTypeId: '',
      actionTypeDescription: '',
      comments: '',
    });
  }

  protected startEditSwapAction(action: SwapActionItem): void {
    if (this.isMutatingSwapAction()) {
      return;
    }

    this.swapActionFormError.set('');
    this.editingSwapActionId.set(action.id);
    this.swapActionForm.set({
      tradeDate: this.toDateInputValue(action.tradeDate),
      effectiveDate: this.toDateInputValue(action.effectiveDate),
      actionTypeId: action.actionTypeId === null ? '' : String(action.actionTypeId),
      actionTypeDescription: this.resolveSwapActionTypeDescription(action.actionTypeId, action.actionTypeDescription),
      comments: action.comments,
    });
  }

  protected updateSwapActionField(field: keyof SwapActionFormState, value: string): void {
    if (field === 'actionTypeId') {
      const description = this.resolveSwapActionTypeDescription(this.toNullableInteger(value), '');
      this.swapActionForm.update((current) => ({
        ...current,
        actionTypeId: value,
        actionTypeDescription: description,
      }));
      return;
    }

    this.swapActionForm.update((current) => ({
      ...current,
      [field]: value,
    }));
  }

  protected submitSwapAction(): void {
    const currentDetail = this.detail();
    if (!currentDetail || this.isMutatingSwapAction()) {
      return;
    }

    const payload = this.toSwapActionUpsertRequest(currentDetail.tradeId || this.tradeId);
    if (!payload) {
      return;
    }

    const editingId = this.editingSwapActionId();
    this.isMutatingSwapAction.set(true);
    this.swapActionFormError.set('');
    this.swapActionMutationSubscription?.unsubscribe();

    if (editingId === null) {
      this.swapActionMutationSubscription = this.swapDetailService.createSwapAction(payload).subscribe({
        next: (createdAction) => {
          this.upsertSwapActionRow(this.withResolvedActionTypeMetadata(createdAction));
          this.isMutatingSwapAction.set(false);
          this.resetSwapActionForm();
          this.modalController.close(this.swapActionModalId);
          window.KTToast?.show({
            message: 'Action added successfully.',
            variant: 'success',
          });
        },
        error: (error: unknown) => {
          this.isMutatingSwapAction.set(false);
          this.swapActionFormError.set(resolveApiErrorMessage('Unable to add action. Please try again.', error));
        },
      });
      return;
    }

    this.swapActionMutationSubscription = this.swapDetailService.updateSwapAction(editingId, payload).subscribe({
      next: () => {
        const currentRow = currentDetail.swapActions.find((item) => item.id === editingId);
        this.upsertSwapActionRow({
          id: editingId,
          tradeId: payload.tradeId,
          tradeDate: payload.tradeDate,
          actionTypeId: payload.actionTypeId,
          actionTypeName: this.resolveSwapActionTypeName(payload.actionTypeId, currentRow?.actionTypeName),
          actionTypeDescription: this.resolveSwapActionTypeDescription(payload.actionTypeId, currentRow?.actionTypeDescription ?? ''),
          comments: payload.comments,
          effectiveDate: payload.effectiveDate,
        });
        this.isMutatingSwapAction.set(false);
        this.resetSwapActionForm();
        this.modalController.close(this.swapActionModalId);
        window.KTToast?.show({
          message: 'Action updated successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingSwapAction.set(false);
        this.swapActionFormError.set(resolveApiErrorMessage('Unable to update action. Please try again.', error));
      },
    });
  }

  protected requestDeleteSwapAction(action: SwapActionItem): void {
    if (this.isMutatingSwapAction()) {
      return;
    }

    this.pendingSwapActionDeleteId.set(action.id);
    this.pendingSwapActionDeleteName.set(action.actionTypeName || `Action ${action.id}`);
    this.swapActionFormError.set('');
    this.modalController.open(this.deleteSwapActionModalId);
  }

  protected cancelDeleteSwapAction(): void {
    if (this.isMutatingSwapAction()) {
      return;
    }

    this.pendingSwapActionDeleteId.set(null);
    this.pendingSwapActionDeleteName.set('');
  }

  protected confirmDeleteSwapAction(): void {
    const actionId = this.pendingSwapActionDeleteId();
    if (actionId === null || this.isMutatingSwapAction()) {
      return;
    }

    this.isMutatingSwapAction.set(true);
    this.swapActionFormError.set('');
    this.swapActionMutationSubscription?.unsubscribe();
    this.swapActionMutationSubscription = this.swapDetailService.deleteSwapAction(actionId).subscribe({
      next: () => {
        this.removeSwapActionRow(actionId);
        this.pendingSwapActionDeleteId.set(null);
        this.pendingSwapActionDeleteName.set('');
        this.isMutatingSwapAction.set(false);
        this.modalController.close(this.deleteSwapActionModalId);
        window.KTToast?.show({
          message: 'Action deleted successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingSwapAction.set(false);
        this.swapActionFormError.set(resolveApiErrorMessage('Unable to delete action. Please try again.', error));
      },
    });
  }

  protected trackAction(index: number, item: SwapActionItem): string {
    return `${item.id}-${item.tradeId}-${index}`;
  }

  protected trackDocument(index: number, item: SwapDocumentItem): string {
    return `${item.id}-${item.parentElementId}-${index}`;
  }

  protected resetAddDocumentForm(): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.documentFormError.set('');
    this.editingDocumentId.set(null);
    this.documentUploadEntries.set([]);
    if (this.documentFilesInput) {
      this.documentFilesInput.nativeElement.value = '';
    }
    this.addDocumentForm.set({
      description: '',
      filename: '',
      files: [],
    });
  }

  protected documentFilenames(documents: readonly SwapDocumentItem[]): string[] {
    return documents.map((document) => document.filename);
  }

  protected renameDocument(document: SwapDocumentItem, filename: string): void {
    this.saveDocumentInline(document, 'filename', filename);
  }

  protected updateDocumentDescription(document: SwapDocumentItem, description: string | null): void {
    this.saveDocumentInline(document, 'description', description);
  }

  private saveDocumentInline(document: SwapDocumentItem, field: 'filename' | 'description', value: string | null): void {
    const currentDetail = this.detail();
    if (!currentDetail || this.isMutatingDocument()) {
      return;
    }

    const payload: DocumentUpdateRequest = {
      parentElementId: currentDetail.tradeId || this.tradeId || null,
      securityId: null,
      description: field === 'description' ? value : document.description || null,
      type: document.type || null,
      filename: field === 'filename' ? (value ?? document.filename) : document.filename,
      ezShareId: document.ezShareId || null,
      ezShareLink: document.ezShareLink || null,
      source: this.documentSource,
    };

    this.isMutatingDocument.set(true);
    const savingDocumentId = field === 'filename' ? this.renamingDocumentId : this.describingDocumentId;
    savingDocumentId.set(document.id);
    this.documentMutationSubscription?.unsubscribe();
    this.documentMutationSubscription = this.swapDetailService.updateDocument(document.id, payload).subscribe({
      next: () => {
        this.isMutatingDocument.set(false);
        savingDocumentId.set(null);
        this.refreshDocumentsByTrade();
        window.KTToast?.show({
          message: field === 'filename' ? `Document renamed to '${value}'.` : 'Document description updated.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingDocument.set(false);
        savingDocumentId.set(null);
        window.KTToast?.show({
          message: resolveApiErrorMessage(
            field === 'filename'
              ? 'Unable to rename document. Please try again.'
              : 'Unable to update document description. Please try again.',
            error,
          ),
          variant: 'destructive',
        });
      },
    });
  }

  protected startEditDocumentRow(row: SwapDocumentItem): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.documentFormError.set('');
    this.editingDocumentId.set(row.id);
    this.addDocumentForm.set({
      description: row.description,
      filename: row.filename,
      files: [],
    });
  }

  protected updateAddDocumentField(field: 'description' | 'filename', value: string): void {
    this.addDocumentForm.update((current) => ({
      ...current,
      [field]: value,
    }));
    this.documentFormError.set('');
  }

  protected onDocumentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];

    this.addDocumentForm.update((current) => ({
      ...current,
      files,
    }));
    this.documentUploadEntries.set(createDocumentUploadEntries(files));
    this.documentFormError.set('');
  }

  protected addOrUpdateDocument(): void {
    const currentDetail = this.detail();
    if (!currentDetail || this.isMutatingDocument()) {
      return;
    }

    const form = this.addDocumentForm();
    const editingDocumentId = this.editingDocumentId();
    if (editingDocumentId !== null) {
      const filename = form.filename.trim();
      if (!filename) {
        this.documentFormError.set('Filename is required.');
        return;
      }

      const currentDocument = currentDetail.documentsAttached.find((document) => document.id === editingDocumentId);
      if (!currentDocument) {
        this.documentFormError.set('Unable to identify the document to update.');
        return;
      }

      const payload: DocumentUpdateRequest = {
        parentElementId: currentDetail.tradeId || this.tradeId || null,
        securityId: null,
        description: form.description.trim() || null,
        type: currentDocument.type || null,
        filename,
        ezShareId: currentDocument.ezShareId || null,
        ezShareLink: currentDocument.ezShareLink || null,
        source: this.documentSource,
      };

      this.isMutatingDocument.set(true);
      this.documentFormError.set('');
      this.documentMutationSubscription?.unsubscribe();
      this.documentMutationSubscription = this.swapDetailService.updateDocument(editingDocumentId, payload).subscribe({
        next: () => {
          this.isMutatingDocument.set(false);
          this.closeAddDocumentModalAfterMutation();
          this.refreshDocumentsByTrade();
          window.KTToast?.show({ message: 'Document metadata updated successfully.', variant: 'success' });
        },
        error: (error: unknown) => {
          this.isMutatingDocument.set(false);
          this.documentFormError.set(resolveApiErrorMessage('Unable to update document. Please try again.', error));
        },
      });

      return;
    }

    if (form.files.length === 0) {
      this.documentFormError.set('Select at least one file to upload.');
      return;
    }

    const reservedFilenames = new Set(
      currentDetail.documentsAttached
        .map((document) => document.filename.trim().toUpperCase())
        .filter((filename) => filename.length > 0),
    );
    const invalidFiles: string[] = [];
    const payloads: DocumentCreateRequest[] = [];

    for (const file of form.files) {
      const extension = this.extractFileExtension(file.name);
      if (!extension || !this.isAllowedDocumentType(extension)) {
        invalidFiles.push(file.name || '(unknown file)');
        continue;
      }

      let generatedFilename = '';
      let attempts = 0;
      do {
        generatedFilename = `${this.generateDocumentCode()}.${extension.slice(1)}`.toUpperCase();
        attempts += 1;
      } while (reservedFilenames.has(generatedFilename) && attempts < 10);

      if (reservedFilenames.has(generatedFilename)) {
        this.documentFormError.set('Unable to generate a unique filename code. Please retry upload.');
        return;
      }
      reservedFilenames.add(generatedFilename);

      payloads.push({
        filename: generatedFilename,
        parentElementId: currentDetail.tradeId || this.tradeId || null,
        securityId: null,
        description: '',
        type: extension,
        source: this.documentSource,
        modifiedBy: this.resolveDocumentModifiedBy(),
        file,
      });
    }

    if (invalidFiles.length > 0) {
      this.documentFormError.set(
        `Unsupported file type: ${invalidFiles.join(', ')}. Allowed types: ${this.allowedDocumentTypes.join(', ')}.`,
      );
      return;
    }

    if (payloads.length === 0) {
      this.documentFormError.set('No valid files were selected for upload.');
      return;
    }

    this.isMutatingDocument.set(true);
    this.documentFormError.set('');
    this.documentMutationSubscription?.unsubscribe();
    this.documentMutationSubscription = uploadDocumentsSequentially(
      payloads,
      (payload) => this.swapDetailService.createDocument(payload),
      (file, status, message) =>
        this.documentUploadEntries.update((entries) => updateDocumentUploadEntry(entries, file, status, message)),
    ).subscribe((outcomes) => {
      const summary = summarizeDocumentUploads(outcomes);
      this.isMutatingDocument.set(false);
      this.refreshDocumentsByTrade();
      window.KTToast?.show(buildDocumentUploadToast(summary));

      if (summary.failed.length === 0) {
        this.closeAddDocumentModalAfterMutation();
        return;
      }

      this.addDocumentForm.update((current) => ({ ...current, files: summary.retryableFiles }));
      if (summary.retryableFiles.length > 0) {
        this.documentFormError.set('Some files could not be uploaded. Upload again to retry only the failed files.');
      }
    });
  }

  protected requestDeleteDocumentRow(row: SwapDocumentItem): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.pendingDocumentDeleteId.set(row.id);
    this.pendingDocumentDeletePreview.set((row.filename || row.description || String(row.id)).trim().slice(0, 120));
  }

  protected cancelDeleteDocumentRow(): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.pendingDocumentDeleteId.set(null);
    this.pendingDocumentDeletePreview.set('');
  }

  protected confirmDeleteDocumentRow(): void {
    const documentId = this.pendingDocumentDeleteId();
    if (documentId === null || this.isMutatingDocument()) {
      return;
    }

    this.isMutatingDocument.set(true);
    this.documentFormError.set('');
    this.documentMutationSubscription?.unsubscribe();
    this.documentMutationSubscription = this.swapDetailService.deleteDocument(documentId).subscribe({
      next: () => {
        this.refreshDocumentsByTrade();
        this.modalController.close(this.deleteDocumentModalId);
        this.pendingDocumentDeleteId.set(null);
        this.pendingDocumentDeletePreview.set('');
        this.isMutatingDocument.set(false);
        window.KTToast?.show({ message: 'Document deleted successfully.', variant: 'success' });
      },
      error: (error: unknown) => {
        this.isMutatingDocument.set(false);
        this.documentFormError.set(resolveApiErrorMessage('Unable to delete document. Please try again.', error));
      },
    });
  }

  protected trackNote(index: number, item: SwapNoteItem): string {
    return `${item.id}-${item.parentId}-${index}`;
  }

  protected updateAddNoteField(field: keyof SwapNoteFormState, value: string): void {
    this.addNoteForm.update((current) => ({
      ...current,
      [field]: value,
    }));
  }

  protected resetAddNoteForm(): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.noteFormError.set('');
    this.editingNoteId.set(null);
    this.addNoteForm.set({ note: '' });
  }

  protected startEditNoteRow(row: SwapNoteItem): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.noteFormError.set('');
    this.editingNoteId.set(row.id);
    this.addNoteForm.set({ note: row.description });
  }

  protected addNoteRow(): void {
    const noteText = this.addNoteForm().note.trim();
    if (!noteText) {
      this.noteFormError.set('Note text is required.');
      return;
    }

    if (noteText.length > 1600) {
      this.noteFormError.set('Note text cannot exceed 1600 characters.');
      return;
    }

    const payload = this.buildNotePayload(noteText);
    if (!payload) {
      this.noteFormError.set('Trade ID is required to save notes.');
      return;
    }

    const editingNoteId = this.editingNoteId();
    this.isMutatingNote.set(true);
    this.noteFormError.set('');
    this.noteMutationSubscription?.unsubscribe();

    if (editingNoteId === null) {
      this.noteMutationSubscription = this.swapDetailService.createNote(payload).subscribe({
        next: (response) => {
          this.upsertNoteInDetail(response);
          this.loadNotes();
          this.resetAddNoteForm();
          this.modalController.close(this.addNoteModalId);
          this.isMutatingNote.set(false);
          window.KTToast?.show({
            message: 'Note added successfully.',
            variant: 'success',
          });
        },
        error: (error: unknown) => {
          this.isMutatingNote.set(false);
          this.noteFormError.set(resolveApiErrorMessage('Unable to add note. Please try again.', error));
        },
      });
      return;
    }

    this.noteMutationSubscription = this.swapDetailService.updateNote(editingNoteId, payload).subscribe({
      next: () => {
        this.updateNoteInDetail(editingNoteId, payload.description, payload.modifiedBy);
        this.loadNotes();
        this.resetAddNoteForm();
        this.modalController.close(this.addNoteModalId);
        this.isMutatingNote.set(false);
        window.KTToast?.show({
          message: 'Note updated successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingNote.set(false);
        this.noteFormError.set(resolveApiErrorMessage('Unable to update note. Please try again.', error));
      },
    });
  }

  protected requestDeleteNoteRow(row: SwapNoteItem): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.pendingNoteDeleteId.set(row.id);
    this.pendingNoteDeletePreview.set(row.description.trim().slice(0, 120));
    this.noteFormError.set('');
    this.modalController.open(this.deleteNoteModalId);
  }

  protected cancelDeleteNoteRow(): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.pendingNoteDeleteId.set(null);
    this.pendingNoteDeletePreview.set('');
  }

  protected confirmDeleteNoteRow(): void {
    const noteId = this.pendingNoteDeleteId();
    if (noteId === null || this.isMutatingNote()) {
      return;
    }

    this.isMutatingNote.set(true);
    this.noteFormError.set('');
    this.noteMutationSubscription?.unsubscribe();
    this.noteMutationSubscription = this.swapDetailService.deleteNote(noteId).subscribe({
      next: () => {
        this.removeNoteFromDetail(noteId);
        this.loadNotes();
        this.modalController.close(this.deleteNoteModalId);
        this.pendingNoteDeleteId.set(null);
        this.pendingNoteDeletePreview.set('');
        this.isMutatingNote.set(false);
        window.KTToast?.show({
          message: 'Note deleted successfully.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingNote.set(false);
        this.noteFormError.set(resolveApiErrorMessage('Unable to delete note. Please try again.', error));
      },
    });
  }

  protected hasPayFormulas(): boolean {
    return (this.detail()?.pay.formulas.length ?? 0) > 0;
  }

  protected hasReceiveFormulas(): boolean {
    return (this.detail()?.receive.formulas.length ?? 0) > 0;
  }

  protected openPayFormulasModal(): void {
    const formulas = this.detail()?.pay.formulas ?? [];
    if (formulas.length === 0) {
      return;
    }

    this.selectedFormulas.set(formulas);
    this.selectedFormulasTitle.set('PAY Formulas');
    this.modalController.open(this.formulasModalId);
  }

  protected openReceiveFormulasModal(): void {
    const formulas = this.detail()?.receive.formulas ?? [];
    if (formulas.length === 0) {
      return;
    }

    this.selectedFormulas.set(formulas);
    this.selectedFormulasTitle.set('RECEIVE Formulas');
    this.modalController.open(this.formulasModalId);
  }

  protected trackFormula(index: number, item: SwapFormulaItem): string {
    return `${item.tradeId}-${item.assetId}-${item.applyDate}-${index}`;
  }

  private loadDetail(): void {
    if (!this.tradeId) {
      this.errorMessage.set('Trade ID is required to load swap detail.');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');
    this.saveError.set('');
    this.saveSuccessMessage.set('');
    this.detailSubscription?.unsubscribe();

    this.detailSubscription = this.swapDetailService.getSwapDetail(this.tradeId).subscribe({
      next: (response) => {
        this.detail.set({
          ...response,
          swapActions: response.swapActions.map((action) => this.withResolvedActionTypeMetadata(action)),
        });
        this.updateDocumentTitle(response.dealId);
        this.refreshDocumentsByTrade();
        this.loadNotes();
        this.hydrateEditableState(response);
        this.hasUnsavedChanges.set(false);
        this.isLoading.set(false);
        this.queueTabsInitialization();
      },
      error: () => {
        this.detail.set(null);
        this.isLoading.set(false);
        this.errorMessage.set('Unable to load swap detail from the API.');
      },
    });
  }

  private queueTabsInitialization(): void {
    requestAnimationFrame(() => {
      this.initializeKTTabs();
    });
  }

  private hydrateEditableState(detail: SwapDetailModel): void {
    this.isHydrating = true;
    this.purposeId.set(detail.swapExtLocal.purposeId);
    this.purposeSubtypeId.set(detail.swapExtLocal.purposeSubtypeId);
    this.comments.set(detail.swapExtLocal.comments);
    this.genericField1.set(this.toEditableNumber(detail.swapExtLocal.genericField1));
    this.genericField2.set(this.toEditableNumber(detail.swapExtLocal.genericField2));
    this.genericField3.set(detail.swapExtLocal.genericField3);
    this.hasTermSheet.set(detail.hasTermSheet || detail.swapExtLocal.hasTermSheet);
    this.swapTheoreticalFee.set(this.toEditableNumber(detail.swapTheoreticalFee ?? detail.swapExtLocal.swapTheoreticalFee));
    this.bpsPerYear.set('');
    this.swapLeg.set('PAY');
    this.syncLegDependentValues(true, detail.tFeeCurrency || detail.swapExtLocal.tFeeCurrency);
    this.isHydrating = false;
  }

  private toSwapExtLocalSaveRequest(detail: SwapDetailModel): SwapExtLocalSaveRequest {
    return {
      parentId: detail.swapExtLocal.parentId,
      purposeId: this.purposeId(),
      purposeSubtypeId: this.purposeSubtypeId(),
      modifiedBy: detail.swapExtLocal.modifiedBy || detail.doneUser,
      dateModified: detail.swapExtLocal.dateModified || new Date().toISOString(),
      swapTheoreticalFee: this.toNullableNumber(this.swapTheoreticalFee()),
      tFeeCurrency: this.tFeeCurrency(),
      comments: this.comments().trim(),
      genericField1: this.toNullableNumber(this.genericField1()),
      genericField2: this.toNullableNumber(this.genericField2()),
      genericField3: this.genericField3().trim(),
    };
  }

  private toSwapFeeUpsertRequest(tradeId: string): SwapFeeUpsertRequest | null {
    const normalizedTradeId = tradeId.trim();
    if (!normalizedTradeId) {
      this.swapFeeFormError.set('Trade ID is required.');
      return null;
    }

    const feeBpsId = this.toNullableInteger(this.swapFeeForm().feeBpsId);
    if (feeBpsId === null || feeBpsId <= 0) {
      this.swapFeeFormError.set('Fee Type is required.');
      return null;
    }

    const value = this.toNullableNumber(this.swapFeeForm().value);
    if (value === null) {
      this.swapFeeFormError.set('Value is required.');
      return null;
    }

    return {
      tradeId: normalizedTradeId,
      feeBpsId,
      value,
    };
  }

  private toSwapActionUpsertRequest(tradeId: string): SwapActionUpsertRequest | null {
    const normalizedTradeId = tradeId.trim();
    if (!normalizedTradeId) {
      this.swapActionFormError.set('Trade ID is required.');
      return null;
    }

    const actionTypeId = this.toNullableInteger(this.swapActionForm().actionTypeId);
    if (actionTypeId === null || actionTypeId <= 0) {
      this.swapActionFormError.set('Action Type is required.');
      return null;
    }

    const tradeDate = this.toApiDateTime(this.swapActionForm().tradeDate);
    if (!tradeDate) {
      this.swapActionFormError.set('Trade Date is required.');
      return null;
    }

    const effectiveDate = this.toApiDateTime(this.swapActionForm().effectiveDate);
    if (!effectiveDate) {
      this.swapActionFormError.set('Effective Date is required.');
      return null;
    }

    return {
      tradeId: normalizedTradeId,
      tradeDate,
      actionTypeId,
      comments: this.swapActionForm().comments.trim(),
      effectiveDate,
    };
  }

  private recalculateTheoreticalFee(): void {
    const currentDetail = this.detail();
    if (!currentDetail) {
      return;
    }

    const bpsPerYear = this.toNullableNumber(this.bpsPerYear());
    if (bpsPerYear === null) {
      return;
    }

    const settlementDate = this.parseCompactDate(currentDetail.settlementDate);
    const maturityDate = this.parseCompactDate(currentDetail.maturityDate);
    if (!settlementDate || !maturityDate) {
      return;
    }

    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const dayCount = (maturityDate.getTime() - settlementDate.getTime()) / millisecondsPerDay;
    if (!Number.isFinite(dayCount) || dayCount <= 0) {
      return;
    }

    const isPayLeg = this.swapLeg() === 'PAY';
    const leg = isPayLeg ? currentDetail.pay : currentDetail.receive;
    if (leg.notional === null) {
      return;
    }

    const yearValue = 365.25 * 10000;
    const fee = Math.round((bpsPerYear * leg.notional * dayCount) / yearValue);

    this.swapTheoreticalFee.set(String(fee));
  }

  private syncLegDependentValues(keepExistingCurrencyFallback: boolean, existingCurrency = ''): void {
    const currentDetail = this.detail();
    if (!currentDetail) {
      return;
    }

    const leg = this.swapLeg() === 'PAY' ? currentDetail.pay : currentDetail.receive;
    if (leg.ccy) {
      this.tFeeCurrency.set(leg.ccy);
      return;
    }

    if (keepExistingCurrencyFallback) {
      this.tFeeCurrency.set(existingCurrency);
      return;
    }

    this.tFeeCurrency.set('');
  }

  private upsertSwapFeeRow(fee: SwapFeeItem): void {
    this.detail.update((currentDetail) => {
      if (!currentDetail) {
        return currentDetail;
      }

      const existingIndex = currentDetail.swapFees.findIndex((item) => item.id === fee.id);
      if (existingIndex === -1) {
        return {
          ...currentDetail,
          swapFees: [...currentDetail.swapFees, fee],
        };
      }

      const updated = [...currentDetail.swapFees];
      updated[existingIndex] = fee;
      return {
        ...currentDetail,
        swapFees: updated,
      };
    });
  }

  private upsertSwapActionRow(action: SwapActionItem): void {
    this.detail.update((currentDetail) => {
      if (!currentDetail) {
        return currentDetail;
      }

      const existingIndex = currentDetail.swapActions.findIndex((item) => item.id === action.id);
      if (existingIndex === -1) {
        return {
          ...currentDetail,
          swapActions: [...currentDetail.swapActions, action],
        };
      }

      const updated = [...currentDetail.swapActions];
      updated[existingIndex] = action;
      return {
        ...currentDetail,
        swapActions: updated,
      };
    });
  }

  private removeSwapFeeRow(feeId: number): void {
    this.detail.update((currentDetail) => {
      if (!currentDetail) {
        return currentDetail;
      }

      return {
        ...currentDetail,
        swapFees: currentDetail.swapFees.filter((item) => item.id !== feeId),
      };
    });
  }

  private removeSwapActionRow(actionId: number): void {
    this.detail.update((currentDetail) => {
      if (!currentDetail) {
        return currentDetail;
      }

      return {
        ...currentDetail,
        swapActions: currentDetail.swapActions.filter((item) => item.id !== actionId),
      };
    });
  }

  private resolveSwapFeeTypeName(feeBpsId: number, fallback = ''): string {
    const matchingOption = this.swapFeeTypes().find((item) => item.id === feeBpsId);
    if (matchingOption?.name) {
      return matchingOption.name;
    }

    const feeWithName = this.detail()?.swapFees.find((item) => item.feeBpsId === feeBpsId && !!item.feeTypeName);
    if (feeWithName?.feeTypeName) {
      return feeWithName.feeTypeName;
    }

    if (fallback.trim()) {
      return fallback.trim();
    }

    return `Fee BPS #${feeBpsId}`;
  }

  private resolveSwapActionTypeName(actionTypeId: number, fallback = ''): string {
    const matchingOption = this.swapActionTypes().find((item) => item.id === actionTypeId);
    if (matchingOption?.name) {
      return matchingOption.name;
    }

    const actionWithName = this.detail()?.swapActions.find((item) => item.actionTypeId === actionTypeId && !!item.actionTypeName);
    if (actionWithName?.actionTypeName) {
      return actionWithName.actionTypeName;
    }

    if (fallback.trim()) {
      return fallback.trim();
    }

    return `Action #${actionTypeId}`;
  }

  private resolveSwapActionTypeDescription(actionTypeId: number | null, fallback = ''): string {
    if (actionTypeId === null) {
      return fallback.trim();
    }

    const matchingOption = this.swapActionTypes().find((item) => item.id === actionTypeId);
    if (matchingOption?.description) {
      return matchingOption.description;
    }

    const actionWithDescription = this.detail()?.swapActions.find(
      (item) => item.actionTypeId === actionTypeId && !!item.actionTypeDescription,
    );
    if (actionWithDescription?.actionTypeDescription) {
      return actionWithDescription.actionTypeDescription;
    }

    return fallback.trim();
  }

  private withResolvedActionTypeMetadata(action: SwapActionItem): SwapActionItem {
    const actionTypeId = action.actionTypeId;
    if (actionTypeId === null) {
      return action;
    }

    return {
      ...action,
      actionTypeName: this.resolveSwapActionTypeName(actionTypeId, action.actionTypeName),
      actionTypeDescription: this.resolveSwapActionTypeDescription(actionTypeId, action.actionTypeDescription),
    };
  }

  private loadSwapFeeTypes(): void {
    this.swapFeeTypesSubscription?.unsubscribe();
    this.swapFeeTypesSubscription = this.swapDetailService.getSwapFeeTypes().subscribe({
      next: (feeTypes) => {
        this.swapFeeTypes.set(feeTypes.filter((item) => item.id > 0 && item.name));
      },
      error: () => {
        this.swapFeeTypes.set([]);
        window.KTToast?.show({
          message: 'Unable to load swap fee types.',
          variant: 'destructive',
        });
      },
    });
  }

  private loadSwapActionTypes(): void {
    this.swapActionTypesSubscription?.unsubscribe();
    this.swapActionTypesSubscription = this.swapDetailService.getSwapActionTypes().subscribe({
      next: (actionTypes) => {
        this.swapActionTypes.set(actionTypes.filter((item) => item.id > 0 && item.name));
      },
      error: () => {
        this.swapActionTypes.set([]);
        window.KTToast?.show({
          message: 'Unable to load swap action types.',
          variant: 'destructive',
        });
      },
    });
  }

  private closeAddDocumentModalAfterMutation(): void {
    requestAnimationFrame(() => this.modalController.close(this.addDocumentModalId));
  }

  private refreshDocumentsByTrade(): void {
    const tradeId = (this.detail()?.tradeId || this.tradeId).trim();
    if (!tradeId) {
      return;
    }

    this.documentsSubscription?.unsubscribe();
    this.documentsSubscription = this.swapDetailService.getDocumentsByTrade(tradeId).subscribe({
      next: (documents) => {
        this.updateDocumentsInDetail(documents);
      },
      error: () => {
        // Keep documents from detail payload if dedicated documents endpoint fails.
      },
    });
  }

  private updateDocumentsInDetail(documents: SwapDocumentItem[]): void {
    this.detail.update((currentDetail) => {
      if (!currentDetail) {
        return currentDetail;
      }

      return {
        ...currentDetail,
        documentsAttached: documents,
      };
    });
  }

  private resolveDocumentModifiedBy(): string {
    const candidate = (this.detail()?.doneUser ?? '').trim();
    return candidate ? candidate.slice(0, 50) : 'FRIDA UI';
  }

  private extractFileExtension(filename: string): string | null {
    const trimmed = filename.trim();
    const lastDot = trimmed.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === trimmed.length - 1) {
      return null;
    }

    return trimmed.slice(lastDot).toLowerCase();
  }

  private isAllowedDocumentType(extension: string): boolean {
    return this.allowedDocumentTypes.includes(extension.toLowerCase());
  }

  private generateDocumentCode(length = 12): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let index = 0; index < length; index += 1) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      result += charset[randomIndex];
    }

    return result;
  }

  private buildNotePayload(description: string): SwapNoteUpsertRequest | null {
    const parentId = this.tradeId.trim();
    if (!parentId) {
      return null;
    }

    const modifiedBy = (this.detail()?.doneUser ?? '').trim() || 'FRIDA UI';

    return {
      parentId: parentId.slice(0, 20),
      source: this.notesSource,
      description,
      modifiedBy: modifiedBy.slice(0, 50),
    };
  }

  private loadNotes(): void {
    if (!this.tradeId) {
      return;
    }

    const tradeId = this.tradeId.trim().toUpperCase();
    this.notesSubscription?.unsubscribe();
    this.notesSubscription = this.swapDetailService.getNotes().subscribe({
      next: (notes) => {
        const mapped = notes.filter(
          (note) => note.parentId.trim().toUpperCase() === tradeId
            && note.source.trim().toUpperCase() === this.notesSource.toUpperCase(),
        );

        this.detail.update((currentDetail) => {
          if (!currentDetail) {
            return currentDetail;
          }

          return {
            ...currentDetail,
            notes: mapped,
          };
        });
      },
      error: () => {
        // Keep existing notes from swap detail payload if notes endpoint fails.
      },
    });
  }

  private upsertNoteInDetail(note: SwapNoteItem): void {
    this.detail.update((currentDetail) => {
      if (!currentDetail) {
        return currentDetail;
      }

      const exists = currentDetail.notes.some((entry) => entry.id === note.id);
      if (!exists) {
        return {
          ...currentDetail,
          notes: [...currentDetail.notes, note],
        };
      }

      return {
        ...currentDetail,
        notes: currentDetail.notes.map((entry) => (entry.id === note.id ? note : entry)),
      };
    });
  }

  private updateNoteInDetail(noteId: number, description: string, modifiedBy: string): void {
    const nowIso = new Date().toISOString();
    this.detail.update((currentDetail) => {
      if (!currentDetail) {
        return currentDetail;
      }

      return {
        ...currentDetail,
        notes: currentDetail.notes.map((entry) => (
          entry.id === noteId
            ? {
                ...entry,
                description,
                modifiedBy,
                dateModified: nowIso,
              }
            : entry
        )),
      };
    });
  }

  private removeNoteFromDetail(noteId: number): void {
    this.detail.update((currentDetail) => {
      if (!currentDetail) {
        return currentDetail;
      }

      return {
        ...currentDetail,
        notes: currentDetail.notes.filter((entry) => entry.id !== noteId),
      };
    });
  }

  private parseCompactDate(value: string): Date | null {
    const compactMatch = /^(\d{4})(\d{2})(\d{2})$/.exec(value.trim());
    if (compactMatch) {
      const [, yearText, monthText, dayText] = compactMatch;
      const year = Number(yearText);
      const month = Number(monthText);
      const day = Number(dayText);
      const parsedCompact = new Date(year, month - 1, day);

      if (
        !Number.isNaN(parsedCompact.getTime())
        && parsedCompact.getFullYear() === year
        && parsedCompact.getMonth() === month - 1
        && parsedCompact.getDate() === day
      ) {
        return parsedCompact;
      }
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private toDateInputValue(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    const compactMatch = /^(\d{4})(\d{2})(\d{2})$/.exec(trimmed);
    if (compactMatch) {
      const [, year, month, day] = compactMatch;
      return `${year}-${month}-${day}`;
    }

    const isoMatch = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
    if (isoMatch) {
      return isoMatch[1];
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    const yyyy = parsed.getFullYear();
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const dd = String(parsed.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private toApiDateTime(value: string): string | null {
    const dateValue = value.trim();
    if (!dateValue) {
      return null;
    }

    const parsed = new Date(`${dateValue}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return `${dateValue}T00:00:00`;
  }

  private toEditableNumber(value: number | null): string {
    return value === null ? '' : String(value);
  }

  private toNullableInteger(value: string): number | null {
    if (!value.trim()) {
      return null;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return null;
    }

    return Math.trunc(parsed);
  }

  private toNullableNumber(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private markDirty(): void {
    if (this.isHydrating) {
      return;
    }

    this.hasUnsavedChanges.set(true);
    this.saveSuccessMessage.set('');
  }

  private initializeKTTabs(): void {
    const tabsElement = this.tabsRoot?.nativeElement;
    if (!tabsElement) {
      return;
    }

    const tabsApi = (window as Window & { KTTabs?: KTTabsConstructor }).KTTabs;
    if (!tabsApi) {
      return;
    }

    tabsApi.createInstances?.();
    this.tabsInstance = tabsApi.getOrCreateInstance?.(tabsElement) ?? null;

    const defaultTab = tabsElement.querySelector<HTMLElement>('[data-kt-tab-toggle].active');
    if (this.tabsInstance && defaultTab) {
      this.tabsInstance.show(defaultTab);
    }
  }

  private updateDocumentTitle(dealId: string): void {
    this.titleService.setTitle(`FRIDA - ${dealId}`);
  }
}
